import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { useResizeDetector } from 'react-resize-detector';

import { BaseColorKeys, SubjectColorKeys } from '../../theme';
import DetailsModal, { Fields, Values } from '../DetailsModal';

import {
  Card,
  CardContent,
  Content,
  ContentText,
  StatusContainer,
  TimeText,
  TimeTextContainer,
} from './index.styles';
import { EventState } from './types';

interface EventCellProps {
  startDatetime: number,
  duration?: number,
  color?: BaseColorKeys | SubjectColorKeys,
  title?: string,
  propertyValues?: Values,
  propertyFields?: Fields,
  state?: EventState,
  showStatus?: boolean,
  fetchProperties?: () => {
    values: Values,
    fields: Fields,
  },
}

const EventCell = ({
  startDatetime,
  duration,
  color = 'primary',
  title = '',
  showStatus = false,
  propertyValues,
  propertyFields,
  state,
  fetchProperties,
}: EventCellProps) => {
  const status = state?.status;
  const buttons = state?.buttons;

  const [eventProperties, setEventProperties] = useState<Values>();
  const [eventFields, setEventFields] = useState<Fields>();

  useEffect(() => {
    if (propertyValues) {
      setEventProperties(propertyValues);
    }
  }, [propertyValues]);

  useEffect(() => {
    if (propertyFields) {
      setEventFields(propertyFields);
    }
  }, [propertyFields]);

  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const openModal = async () => {
    setModalOpen(true);
    if (fetchProperties) {
      setLoading(true);
      setEventProperties((await fetchProperties()).values);
      setEventFields((await fetchProperties()).fields);
      setLoading(false);
    }
  };
  const closeModal = () => {
    setModalOpen(false);
  };

  const startTime = dayjs(startDatetime).format('h:mm a');
  const endTime = duration ? dayjs(startDatetime).add(duration, 'minute').format('h:mm a') : undefined;

  const { width = 0, ref } = useResizeDetector();

  return (
    <>
      <Card ref={ref} onClick={openModal}>
        <CardContent>
          <TimeTextContainer color={color}>
            <TimeText variant="subtitle2">
              {width > 144 && endTime ? `${startTime} - ${endTime}` : startTime}
            </TimeText>
          </TimeTextContainer>
          <Content color={color} showStatus={showStatus}>
            <ContentText variant="subtitle2">
              {title}
            </ContentText>
          </Content>
          {(showStatus && status) ? (
            <StatusContainer color={status.color ?? 'gray'} showStatus={showStatus}>
              {status.text}
              {status.icon}
            </StatusContainer>
          )
            : (state?.state === 'imminent' && status && (
              <StatusContainer color={status.color ?? 'gray'} showStatus={showStatus}>
                {status.icon}
              </StatusContainer>
            ))}
        </CardContent>
      </Card>
      <DetailsModal
        subtitle={`${dayjs(startDatetime).format('D MMMM YYYY')}\u{00A0} \u{000B7} \u{00A0}${startTime}${endTime ? ` - ${endTime}` : ''}`}
        open={modalOpen}
        handleClose={closeModal}
        title={title}
        values={eventProperties}
        fields={eventFields}
        loading={loading}
        buttons={buttons}
        titleColor={color}
      />
    </>
  );
};

export default EventCell;
